// initialize bugsnag ASAP, before other
/* eslint-disable import/order */
import Bugsnag, { type OnErrorCallback } from '@bugsnag/js';
import BugsnagPluginReact, { type BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import { type User } from '@auth0/auth0-react';
/* eslint-enable import/order */

import LogRocket from 'logrocket';
import * as React from 'react';

import { APP_VERSION, isLocal, isProdReview, isStage, isStageReview } from './env';
import { getItem } from './storage';

// these errors are reported a lot, but typically there is no action to take on
// our part. ignore these to reduce the noise to bugsnag.
const errorsToIgnore = [
  'Failed to update a ServiceWorker for scope',
  'ResizeObserver loop completed with undelivered notifications',
];

const MODE = {
  local: 'local',
  stage: 'stage',
  stageReview: 'stage-review',
  production: 'production',
  prodReview: 'prod-review',
};

let ErrorBoundary: BugsnagErrorBoundary = ({ children }: { children: React.ReactElement }) =>
  children;

let releaseStage: string;
if (isLocal) {
  releaseStage = MODE.local;
} else if (isStage) {
  releaseStage = MODE.stage;
} else if (isStageReview) {
  releaseStage = MODE.stageReview;
} else if (isProdReview) {
  releaseStage = MODE.prodReview;
} else {
  releaseStage = MODE.production;
}

/**
 * To enable units tests to operate normally, we try/catch JSDOM issues with bugsnag.
 * Related issues:
 *   - https://github.com/facebook/jest/issues/1909
 *   - https://github.com/bugsnag/bugsnag-js/issues/452
 */
try {
  Bugsnag.start({
    apiKey: '60de0015873d1e2ecc355521123f6bdf',
    appVersion: APP_VERSION,
    enabledReleaseStages: [MODE.stage, MODE.stageReview, MODE.production, MODE.prodReview],
    plugins: [new BugsnagPluginReact()],
    releaseStage,
    onError(event) {
      // Don't do anything if we're running on localhost
      if (isLocal) {
        return false;
      }

      // Some errors may be caught by Bugsnag directly, so include logic to filter out errors we know need to be ignored here
      if (errorsToIgnore.some(e => event.originalError.includes(e))) {
        // Don't log out the skipped errors in prod, but otherwise we can show them
        if (releaseStage !== MODE.production) {
          console.warn('Not reporting to bugsnag: ', event.originalError);
        }
        return false;
      }

      const customerId = getItem('customerId');
      event.addMetadata('Horizon', { customerId });

      // Addn LogRocket session URL to any bugsnag issues
      if (releaseStage === MODE.production) {
        event.addMetadata('LogRocket', {
          sessionURL: LogRocket.sessionURL,
        });
      }
    },
  });

  ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React) ?? ErrorBoundary;
} catch (error) {
  console.error(error.message);
}

const setBugsnagUser = ({ email, sub }: User) => Bugsnag.setUser(sub, email);

const notifyBugsnag = (error: Error | string, onError?: OnErrorCallback) => {
  const message = typeof error === 'string' ? error : error.message;

  if (releaseStage !== MODE.local) {
    if (errorsToIgnore.some(e => message.includes(e))) {
      // Don't log out the skipped errors in prod, but otherwise we can show them
      if (releaseStage !== MODE.production) {
        console.warn('Not reporting to bugsnag: ', error);
      }
      return;
    }

    Bugsnag.notify(error, onError);
  } else {
    console.info('[message for bugsnag]:', error);
  }
};

export { ErrorBoundary, notifyBugsnag, setBugsnagUser };
